import { jsPDF } from "jspdf";

// ─── SolFri palette ───────────────────────────────────────────────────────────
const C = {
  cream:      [253, 248, 243],
  creamDark:  [245, 237, 228],
  brown:      [92, 61, 46],
  brownSoft:  [160, 128, 112],
  terracotta: [196, 133, 106],
  terraDark:  [160, 103, 58],
  sage:       [122, 158, 126],
  sageMid:    [150, 184, 154],
  sageLight:  [184, 212, 176],
  amber:      [146, 64, 14],
  amberBg:    [255, 251, 235],
  red:        [220, 38, 38],
  redBg:      [254, 242, 242],
  white:      [255, 255, 255],
};

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 18;
const CONTENT_W = PAGE_W - MARGIN * 2;
const FOOTER_Y = PAGE_H - 16;

// ─── Draw mini bonsai (vector) at x,y with given scale ───────────────────────
function drawBonsai(doc, x, y, s = 1) {
  const e = (cx, cy, rx, ry, color) => {
    doc.setFillColor(...color);
    doc.ellipse(x + cx * s, y + cy * s, rx * s, ry * s, "F");
  };
  // trunk
  doc.setDrawColor(...C.terraDark);
  doc.setLineWidth(1.6 * s);
  doc.line(x + 10 * s, y + 20 * s, x + 9 * s, y + 10 * s);
  doc.setLineWidth(0.9 * s);
  doc.line(x + 9.3 * s, y + 13 * s, x + 4 * s, y + 10 * s);
  doc.line(x + 9.2 * s, y + 12 * s, x + 15 * s, y + 9 * s);
  // canopy
  e(9, 7, 4.6, 3.4, C.sage);
  e(7, 6, 2.8, 2.1, C.sageMid);
  e(11, 5.6, 2.4, 1.8, C.sageLight);
  e(3.5, 9.5, 3, 2.2, C.sage);
  e(15.5, 8.5, 3, 2.2, C.sage);
  e(2.5, 9, 1.8, 1.3, C.sageLight);
  e(16.5, 8, 1.8, 1.3, C.sageLight);
  // pot
  doc.setFillColor(...C.terracotta);
  doc.rect(x + 6.5 * s, y + 20 * s, 7 * s, 1.2 * s, "F");
  doc.setFillColor(...C.terraDark);
  const potPath = [
    [x + 7 * s, y + 21.2 * s],
    [x + 6.5 * s, y + 24 * s],
    [x + 13.5 * s, y + 24 * s],
    [x + 13 * s, y + 21.2 * s],
  ];
  doc.triangle(potPath[0][0], potPath[0][1], potPath[1][0], potPath[1][1], potPath[2][0], potPath[2][1], "F");
  doc.triangle(potPath[0][0], potPath[0][1], potPath[2][0], potPath[2][1], potPath[3][0], potPath[3][1], "F");
}

// ─── Footer on every page ─────────────────────────────────────────────────────
function drawFooter(doc, pageNum, totalPages, crisisLine) {
  doc.setDrawColor(...C.creamDark);
  doc.setLineWidth(0.4);
  doc.line(MARGIN, FOOTER_Y - 4, PAGE_W - MARGIN, FOOTER_Y - 4);
  doc.setFontSize(7);
  doc.setTextColor(...C.brownSoft);
  doc.setFont("helvetica", "normal");
  doc.text("Generated by SolFri — a supportive AI companion, not a substitute for professional care.", MARGIN, FOOTER_Y);
  doc.text(`In crisis? ${crisisLine}`, MARGIN, FOOTER_Y + 3.5);
  doc.text(`Page ${pageNum} of ${totalPages}`, PAGE_W - MARGIN, FOOTER_Y + 1.5, { align: "right" });
}

// ─── Section heading ──────────────────────────────────────────────────────────
function sectionHeading(doc, title, y) {
  doc.setFillColor(...C.terraDark);
  doc.rect(MARGIN, y, 2.2, 6, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...C.brown);
  doc.text(title.toUpperCase(), MARGIN + 5, y + 4.6);
  return y + 11;
}

// ─── Main generator ───────────────────────────────────────────────────────────
export function generatePdfReport({ sessions, userName, treeStage, stageLabel, crisisLine = "SADAG 0800 456 789 (free, 24/7)" }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = 0;

  const ensureSpace = (needed) => {
    if (y + needed > FOOTER_Y - 8) {
      doc.addPage();
      y = MARGIN + 4;
    }
  };

  // ═══ HEADER BAND ═══
  doc.setFillColor(...C.cream);
  doc.rect(0, 0, PAGE_W, PAGE_H, "F");
  doc.setFillColor(...C.terracotta);
  doc.rect(0, 0, PAGE_W, 42, "F");
  doc.setFillColor(...C.terraDark);
  doc.rect(0, 42, PAGE_W, 1.6, "F");

  drawBonsai(doc, MARGIN, 8, 1.05);

  doc.setFont("times", "bold");
  doc.setFontSize(26);
  doc.setTextColor(...C.white);
  doc.text("SOLFRI", MARGIN + 28, 21);
  doc.setFont("times", "italic");
  doc.setFontSize(10);
  doc.text("find your peace", MARGIN + 28, 28);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("WELLNESS REPORT", PAGE_W - MARGIN, 18, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const genDate = new Date().toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" });
  doc.text(`Generated ${genDate}`, PAGE_W - MARGIN, 24, { align: "right" });

  y = 52;

  // ═══ CLIENT SUMMARY CARD ═══
  doc.setFillColor(...C.white);
  doc.setDrawColor(...C.creamDark);
  doc.setLineWidth(0.4);
  doc.roundedRect(MARGIN, y, CONTENT_W, 26, 3, 3, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...C.brownSoft);
  doc.text("CLIENT", MARGIN + 6, y + 7);
  doc.text("TOTAL SESSIONS", MARGIN + 70, y + 7);
  doc.text("GROWTH STAGE", MARGIN + 122, y + 7);

  doc.setFontSize(13);
  doc.setTextColor(...C.brown);
  doc.text(userName || "Anonymous", MARGIN + 6, y + 15);
  doc.text(String(sessions.length), MARGIN + 70, y + 15);
  doc.setTextColor(122, 158, 126);
  doc.text(`Stage ${treeStage} — ${stageLabel}`, MARGIN + 122, y + 15);

  const totalExchanges = sessions.reduce((s, x) => s + (x.exchanges?.length || 0), 0);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...C.brownSoft);
  doc.text(`${totalExchanges} total exchanges recorded across all sessions`, MARGIN + 6, y + 22);

  y += 34;

  // ═══ EMOTIONAL OVERVIEW ═══
  y = sectionHeading(doc, "Emotional Overview", y);

  const allEmotions = sessions.flatMap(s => (s.exchanges || []).map(e => e.emotion).filter(Boolean));
  const counts = {};
  allEmotions.forEach(em => { const k = em.toLowerCase(); counts[k] = (counts[k] || 0) + 1; });
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const maxCount = sorted.length ? sorted[0][1] : 1;

  if (sorted.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9.5);
    doc.setTextColor(...C.brownSoft);
    doc.text("No emotional data recorded yet.", MARGIN, y + 4);
    y += 12;
  } else {
    const barMaxW = CONTENT_W - 62;
    sorted.forEach(([emotion, count]) => {
      ensureSpace(9);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...C.brown);
      const label = emotion.charAt(0).toUpperCase() + emotion.slice(1);
      doc.text(label.length > 22 ? label.slice(0, 22) + "…" : label, MARGIN, y + 4);
      // bar
      const barW = Math.max((count / maxCount) * barMaxW, 4);
      doc.setFillColor(...C.creamDark);
      doc.roundedRect(MARGIN + 44, y, barMaxW, 5.4, 2.4, 2.4, "F");
      doc.setFillColor(...C.terracotta);
      doc.roundedRect(MARGIN + 44, y, barW, 5.4, 2.4, 2.4, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(...C.brownSoft);
      doc.text(`${count}×`, MARGIN + 46 + barMaxW, y + 4);
      y += 8.5;
    });
    y += 4;
  }

  // ═══ RECURRING PATTERNS ═══
  ensureSpace(30);
  y = sectionHeading(doc, "Recurring Patterns", y);
  const patterns = [...new Set(sessions.flatMap(s => (s.exchanges || []).map(e => e.pattern).filter(Boolean)))].slice(0, 6);
  if (patterns.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9.5);
    doc.setTextColor(...C.brownSoft);
    doc.text("No recurring patterns identified yet.", MARGIN, y + 2);
    y += 10;
  } else {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    patterns.forEach(p => {
      const lines = doc.splitTextToSize(p, CONTENT_W - 8);
      ensureSpace(lines.length * 4.6 + 4);
      doc.setFillColor(...C.sage);
      doc.circle(MARGIN + 1.5, y + 1.6, 1.1, "F");
      doc.setTextColor(...C.brown);
      doc.text(lines, MARGIN + 6, y + 3);
      y += lines.length * 4.6 + 3;
    });
    y += 3;
  }

  // ═══ GENTLE OBSERVATIONS (honesty moments) ═══
  const truths = [...new Set(sessions.flatMap(s => (s.exchanges || []).map(e => e.gentle_truth).filter(Boolean)))].slice(0, 5);
  if (truths.length > 0) {
    ensureSpace(28);
    y = sectionHeading(doc, "Gentle Observations", y);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(...C.brownSoft);
    doc.text("Moments where SolFri noticed possible contradictions or avoidance — useful therapeutic entry points.", MARGIN, y);
    y += 6;
    truths.forEach(t => {
      const lines = doc.splitTextToSize(t, CONTENT_W - 14);
      ensureSpace(lines.length * 4.4 + 9);
      doc.setFillColor(...C.amberBg);
      doc.setDrawColor(253, 230, 138);
      doc.roundedRect(MARGIN, y, CONTENT_W, lines.length * 4.4 + 6, 2, 2, "FD");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...C.amber);
      doc.text(lines, MARGIN + 5, y + 5);
      y += lines.length * 4.4 + 9;
    });
    y += 2;
  }

  // ═══ SESSION DETAILS ═══
  ensureSpace(24);
  y = sectionHeading(doc, "Session Details", y);

  sessions.forEach((s, i) => {
    ensureSpace(20);
    // Session header pill
    doc.setFillColor(...C.creamDark);
    doc.roundedRect(MARGIN, y, CONTENT_W, 9, 2, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...C.terraDark);
    doc.text(`Session ${sessions.length - i}`, MARGIN + 4, y + 6);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...C.brownSoft);
    doc.text(`${s.startedAt || ""}   •   ${(s.exchanges || []).length} exchange${(s.exchanges || []).length !== 1 ? "s" : ""}`, PAGE_W - MARGIN - 4, y + 6, { align: "right" });
    y += 13;

    (s.exchanges || []).forEach((ex, j) => {
      const shared = doc.splitTextToSize(`"${ex.userMessage}"`, CONTENT_W - 10);
      const emp = doc.splitTextToSize(ex.empathy || "", CONTENT_W - 10);
      const stratLines = (ex.strategies || []).map(st => doc.splitTextToSize(`•  ${st.title}: ${st.description}`, CONTENT_W - 14));
      const stratHeight = stratLines.reduce((h, l) => h + l.length * 4 + 1.5, 0);
      const blockH = 8 + shared.length * 4.2 + emp.length * 4.2 + stratHeight + (ex.crisis ? 8 : 0) + 8;
      ensureSpace(Math.min(blockH, 100));

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(...C.sage);
      doc.text(`EXCHANGE ${j + 1}  —  ${(ex.emotion || "").toUpperCase()}`, MARGIN + 3, y);
      y += 5;

      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.setTextColor(...C.brownSoft);
      doc.text(shared, MARGIN + 3, y);
      y += shared.length * 4.2 + 2;

      ensureSpace(emp.length * 4.2 + 4);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...C.brown);
      doc.text(emp, MARGIN + 3, y);
      y += emp.length * 4.2 + 2.5;

      stratLines.forEach(l => {
        ensureSpace(l.length * 4 + 3);
        doc.setFontSize(8.5);
        doc.setTextColor(...C.brownSoft);
        doc.text(l, MARGIN + 6, y);
        y += l.length * 4 + 1.5;
      });

      if (ex.crisis) {
        ensureSpace(10);
        doc.setFillColor(...C.redBg);
        doc.roundedRect(MARGIN + 3, y - 1, CONTENT_W - 6, 7, 1.5, 1.5, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(...C.red);
        doc.text("CRISIS INDICATOR flagged in this exchange", MARGIN + 6, y + 3.5);
        y += 9;
      }
      y += 3;
    });
    y += 3;
  });

  // ═══ FOOTERS ═══
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    drawFooter(doc, p, totalPages, crisisLine);
  }

  return doc;
}

// ─── Download helper ──────────────────────────────────────────────────────────
export function downloadPdfReport(opts) {
  const doc = generatePdfReport(opts);
  doc.save(`solfri-wellness-report-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ─── Share helper (native share with PDF file, falls back to download) ───────
export async function sharePdfReport(opts) {
  const doc = generatePdfReport(opts);
  const blob = doc.output("blob");
  const file = new File([blob], `solfri-wellness-report-${new Date().toISOString().slice(0, 10)}.pdf`, { type: "application/pdf" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: "My SolFri Wellness Report" });
      return "shared";
    } catch (e) {
      if (e.name === "AbortError") return "cancelled";
    }
  }
  doc.save(file.name);
  return "downloaded";
}
